/*
 * Getypte smileys worden emoji.
 *
 * Alleen wanneer de smiley áf is en direct voor de cursor staat, en alleen na
 * een spatie of aan het begin van de regel. Die laatste eis is er voor
 * "https://..." - daar staat een dubbele punt tegen een letter aan, en zonder
 * die regel werd elke link een verbaasd gezicht.
 */

/*
 * DE CODES TUSSEN HAAKJES (29-09-2026)
 *
 * Swift vervangt LAN Messenger, en wie daarvoor MSN of Skype gebruikte typt
 * (Y) voor een duim. Die codes kwamen er hier bij, plus de gezichtjes die MSN
 * had en de gewone lijst hierboven nog niet: :$ :@ :S ^_^ -_- en dergelijke.
 * Elke nieuwe code komt uit op een emoji die ook in de kiezer van Swift staat
 * (EmojiPicker.tsx), zodat een getypte duim dezelfde is als een aangeklikte. Op
 * een na: (U) is 💔, net als het </3 dat er al was.
 *
 * Bewust NIET, omdat ze botsen met gewone tekst:
 *   (c) en (C)   copyright, "(c) 2026" - in MSN was dit koffie; hier (koffie)
 *   (b) (i)      opsommingen "(a) ... (b) ..." - in MSN bier en een lampje;
 *                hier (bier) en (idee)
 *   (a) (e) (s)  idem
 *   (u)          "u" in een zin; alleen (U) is een gebroken hart
 * De letters die wel meedoen (y, n, l, h, k) staan zelden alleen tussen haakjes.
 * En zoals alle smileys hier: alleen na een spatie of aan het begin van de
 * regel, dus "fiets(y)" blijft staan.
 *
 * Woordcodes in het Nederlands én het Engels, want Skype leerde de Engelse.
 */

/** Langste eerst, zodat ":-)" niet als ":" plus ")" wordt gelezen en `:-(`
 *  niet door `:(` wordt ingehaald. */
const SMILEYS: Array<[string, string]> = [
  // Woordcodes (Skype, en de Nederlandse tegenhangers).
  ["(fingerscrossed)", "🤞"],
  ["(handshake)", "🤝"],
  ["(thinking)", "🤔"],
  ["(duimpje)", "👍"],
  ["(applaus)", "👏"],
  ["(slapen)", "😴"],
  ["(sleepy)", "😴"],
  ["(coffee)", "☕"],
  ["(koffie)", "☕"],
  ["(muscle)", "💪"],
  ["(spierballen)", "💪"],
  ["(party)", "🎉"],
  ["(feest)", "🎉"],
  ["(heart)", "❤️"],
  ["(hartje)", "❤️"],
  ["(think)", "🤔"],
  ["(denk)", "🤔"],
  ["(clap)", "👏"],
  ["(wave)", "👋"],
  ["(zwaai)", "👋"],
  ["(beer)", "🍺"],
  ["(bier)", "🍺"],
  ["(cool)", "😎"],
  ["(yawn)", "🥱"],
  ["(gaap)", "🥱"],
  ["(rofl)", "🤣"],
  ["(star)", "⭐"],
  ["(ster)", "⭐"],
  ["(idea)", "💡"],
  ["(idee)", "💡"],
  ["(pray)", "🙏"],
  ["(bedankt)", "🙏"],
  ["(fire)", "🔥"],
  ["(vuur)", "🔥"],
  ["(poop)", "💩"],
  ["(pizza)", "🍕"],
  ["(yes)", "👍"],
  ["(cry)", "😢"],
  ["(huil)", "😢"],
  ["(lol)", "😂"],
  ["(100)", "💯"],
  ["(ok)", "👌"],
  ["(no)", "👎"],
  // MSN-letters. Hoofd- en kleine letter, zoals MSN ze allebei kende.
  ["(Y)", "👍"],
  ["(y)", "👍"],
  ["(N)", "👎"],
  ["(n)", "👎"],
  ["(L)", "❤️"],
  ["(l)", "❤️"],
  ["(H)", "😎"],
  ["(h)", "😎"],
  ["(K)", "😘"],
  ["(k)", "😘"],
  ["(U)", "💔"], // niet (u): dat is gewoon "u" in een zin
  ["(*)", "⭐"],
  // Gezichtjes die MSN had en de lijst hieronder nog niet.
  [":'-D", "😂"],
  [":'D", "😂"],
  [":-$", "😊"],
  [":-@", "😡"],
  [":-S", "😖"],
  [":-s", "😖"],
  [":-X", "🤐"],
  [":-x", "🤐"],
  [":-#", "🤐"],
  [":-?", "🤔"],
  ["|-)", "😴"],
  ["8-|", "🤓"],
  ["B-)", "😎"],
  ["^_^", "😊"],
  ["-_-", "😑"],
  [">:-(", "😠"],
  [":$", "😊"],
  [":@", "😡"],
  [":S", "😖"],
  [":s", "😖"],
  [":X", "🤐"],
  [":x", "🤐"],
  ["^^", "😊"],
  // De oorspronkelijke lijst.
  [":'-(", "😢"],
  [":'(", "😢"],
  ["</3", "💔"],
  [">:(", "😠"],
  ["8-)", "😎"],
  [":-)", "🙂"],
  [":-D", "😃"],
  [":-(", "🙁"],
  [":-P", "😛"],
  [":-p", "😛"],
  [":-O", "😮"],
  [":-o", "😮"],
  [":-|", "😐"],
  [":-*", "😘"],
  [":-/", "😕"],
  [";-)", "😉"],
  ["<3", "❤️"],
  ["xD", "😆"],
  ["XD", "😆"],
  [":)", "🙂"],
  [":D", "😃"],
  [":(", "🙁"],
  [":P", "😛"],
  [":p", "😛"],
  [":O", "😮"],
  [":o", "😮"],
  [":|", "😐"],
  [":*", "😘"],
  [":/", "😕"],
  [";)", "😉"],
];

/**
 * Kijkt naar de tekst vlak vóór de cursor en vervangt een afgemaakte smiley.
 *
 * Geeft null terug als er niets te vervangen valt - dan hoeft de aanroeper de
 * cursor ook niet te verzetten, en dat scheelt een hoop geknipper.
 */
export function vervangSmiley(
  tekst: string,
  cursor: number,
): { tekst: string; cursor: number } | null {
  const voor = tekst.slice(0, cursor);

  for (const [smiley, emoji] of SMILEYS) {
    if (!voor.endsWith(smiley)) continue;

    /* Wat staat er vlak vóór de smiley? Aan het begin van de regel of na een
       spatie is het er een; tegen een woord aan is het onderdeel van iets
       anders - een url, een tijdsaanduiding, een gezicht in code. */
    const ervoor = voor.slice(0, voor.length - smiley.length);
    if (ervoor.length > 0 && !/\s$/.test(ervoor)) continue;

    return {
      tekst: ervoor + emoji + tekst.slice(cursor),
      cursor: ervoor.length + emoji.length,
    };
  }
  return null;
}
